// ----------------------------------------------------------------------
// <copyright file="Izenda.SettingStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import fetcher from 'fetchers/Izenda.SystemSettingFetcher.js';
import SettingLevel from './models/Izenda.SettingLevel';
import uiState from 'logic/Izenda.UiState.js';
import { action } from 'mobx';

import ContextMenuStore from './Izenda.ContextMenuStore';
import LicenseCheckerStore from './Izenda.LicenseCheckerStore';
import DataModelStore from './Izenda.DataModelStore';
import AdvancedSettingsStore from './Izenda.AdvancedSettingsStore';
import TenantManagementStore from './Izenda.TenantManagementStore';
import SchedulingSearchStore from 'logic/Izenda.SchedulingSearchStore';
import EmailConfigurationStore from 'logic/Izenda.EmailConfigurationStore';
import CachingConfigurationStore from 'logic/Izenda.CachingConfigurationStore';
import GoogleMapConfigurationStore from 'logic/Izenda.GoogleMapConfigurationStore';
import ExportingConfigurationStore from 'logic/Izenda.ExportingConfigurationStore';
import ReportConfigurationStore from 'logic/Izenda.ReportConfigurationStore';
import MachineLearningStore from 'logic/Izenda.MachineLearningStore';
import NlqConfigurationStore from 'logic/Izenda.NlqConfigurationStore';
import systemSettingFetcher from 'fetchers/Izenda.SystemSettingFetcher';
import { forEach, invoke } from 'lodash/fp';

const disablePages = ['setupAndLicense', 'tenantManagement', 'copyManagement', 'securityPolicies', 'databaseMapping', 'cachingConfiguration', 'exportingConfiguration'];
const onlyShowTenant = [];

export default class SettingsStore {
  settingLevel: SettingLevel;
  contextMenu: ContextMenuStore;
  licenseChecker: LicenseCheckerStore;
  dataModel: DataModelStore;
  advanceSettings: AdvancedSettingsStore;
  tenantManagement: TenantManagementStore;
  schedulingSearch: SchedulingSearchStore;
  emailConfiguration: EmailConfigurationStore;
  machineLearning: MachineLearningStore;
  nlqConfigurationStore: NlqConfigurationStore;
  reportConfiguration: ReportConfigurationStore;
  cachingConfiguration: CachingConfigurationStore;

  constructor(noDirtyFnRegistration: boolean) {
    this.settingLevel = new SettingLevel();

    // Init substores
    this.contextMenu = new ContextMenuStore();
    this.licenseChecker = new LicenseCheckerStore();
    this.dataModel = new DataModelStore();
    this.advanceSettings = new AdvancedSettingsStore();
    this.tenantManagement = new TenantManagementStore(noDirtyFnRegistration);
    this.schedulingSearch = new SchedulingSearchStore();
    this.emailConfiguration = new EmailConfigurationStore(noDirtyFnRegistration);
    this.machineLearning = new MachineLearningStore();
    this.nlqConfigurationStore = new NlqConfigurationStore();
    this.reportConfiguration = new ReportConfigurationStore(noDirtyFnRegistration);
    this.cachingConfiguration = new CachingConfigurationStore(noDirtyFnRegistration);
    this.googleMapConfiguration = new GoogleMapConfigurationStore(noDirtyFnRegistration);
    this.exportingConfiguration = new ExportingConfigurationStore(noDirtyFnRegistration);
    forEach(invoke('init'), this.getInstances());
  }

  destroy() {
    forEach(invoke('destroy'), this.getInstances());
  }

  getInstances() {
    return [
      this.contextMenu,
      this.licenseChecker,
      this.dataModel,
      this.advanceSettings,
      this.tenantManagement,
      this.schedulingSearch,
      this.emailConfiguration,
      this.reportConfiguration,
      this.cachingConfiguration,
      this.machineLearning,
      this.nlqConfigurationStore,
      this.exportingConfiguration,
      this.googleMapConfiguration
    ];
  }

  /**
   * Load Setting Level
   */
  @action
  loadSettingLevel() {
    fetcher
      .getSystemMode()
      .then(systemMode => {
        this.settingLevel.loadInitial(systemMode);
        !systemMode && this.loadTenants();
      });
  }

  /**
   * Load tenants
   */
  loadTenants(shouldLoadDefaultTenant) {
    return fetcher
      .getTenants()
      .then(this.settingLevel.readTenants.bind(this.settingLevel, shouldLoadDefaultTenant));
  }

  /**
   * Change system mode
   */
  @action
  changeSystemMode(change) {
    return change.value === 'Tenant'
      ? this.loadTenants(true)
      : this.settingLevel.setSelectedTenant(null);
  }

  @action
  onlyShowTenant(change) {
    uiState.onlyShowTenant = change;
    if (change) {
      uiState.enableSystemSetting(change);
      !uiState.tenantId && this.loadTenants(true);
    }
  }

  @action
  checkSettingLevel(newLink) {
    newLink = newLink || this.contextMenu.contextMenuActiveLink;
    uiState.setDisableSettingLevel(disablePages.indexOf(newLink) > -1);
    this.onlyShowTenant(onlyShowTenant.indexOf(newLink) > -1);
  }

  /**
   * Check if database had already setup
   * @returns {type}
   */
  checkDatabaseSetup () {
    return systemSettingFetcher.databaseSetup();
  }
}



// WEBPACK FOOTER //
// ./ui/logic/Izenda.SettingsStore.js